import { useQuery, useMutation } from '@tanstack/react-query';

import {
  getTemporaryDocList,
  getTemporaryDocDetail,
  deleteTemporaryDoc,
  getDocumentReceiverList,
  getDocumentExecutorList,
  getDocumentReferenceDocList,
  getDocumentFileList,
} from '@entities/document/api/document';
import { getEmployeeInfo } from '@entities/employee/api/employees';

/**
 * 임시저장 문서 목록 조회 Query
 * @param {Object} params - 검색 조건 (date1, date2, title, name)
 * @param {Object} options - React Query 옵션
 */
function useGetTemporaryDocListQuery(params = {}, options = {}) {
  return useQuery({
    queryKey: ['temporaryDocList', JSON.stringify(params)],
    queryFn: () => getTemporaryDocList(params),
    // LNB 재진입 시 최신 목록 보장
    staleTime: 0,
    refetchOnMount: 'always',
    ...options,
  });
}

/**
 * 임시저장 문서 삭제 Mutation
 * @param {Object} options - React Query 옵션
 */
function useDeleteTemporaryDocMutation(options = {}) {
  return useMutation({
    mutationFn: (docIdx) => deleteTemporaryDoc(docIdx),
    ...options,
  });
}

/**
 * 임시저장 문서 상세 조회용 Query Key 및 QueryFn
 * queryClient.fetchQuery에서 사용
 */
const temporaryDocDetailQueryOptions = (docIdx) => ({
  queryKey: ['temporaryDocDetail', docIdx],
  queryFn: () => getTemporaryDocDetail(docIdx),
});

/**
 * 문서 수신자 목록 조회용 Query Key 및 QueryFn
 * queryClient.fetchQuery에서 사용
 */
const documentReceiverListQueryOptions = (docIdx) => ({
  queryKey: ['documentReceiverList', docIdx],
  queryFn: () => getDocumentReceiverList(docIdx),
});

/**
 * 문서 시행자 목록 조회용 Query Key 및 QueryFn
 * queryClient.fetchQuery에서 사용
 */
const documentExecutorListQueryOptions = (docIdx) => ({
  queryKey: ['documentExecutorList', docIdx],
  queryFn: () => getDocumentExecutorList(docIdx),
});

/**
 * 문서 참조문서 목록 조회용 Query Key 및 QueryFn
 * queryClient.fetchQuery에서 사용
 */
const documentReferenceDocsQueryOptions = (docIdx) => ({
  queryKey: ['documentReferenceDocs', docIdx],
  queryFn: () => getDocumentReferenceDocList(docIdx),
});

/**
 * 문서 첨부파일 목록 조회용 Query Key 및 QueryFn
 * queryClient.fetchQuery에서 사용
 */
const documentFileListQueryOptions = (docIdx) => ({
  queryKey: ['documentFileList', docIdx],
  queryFn: () => getDocumentFileList(docIdx),
});

/**
 * 직원 정보 조회용 Query Key 및 QueryFn
 * queryClient.fetchQuery에서 사용
 */
const employeeInfoQueryOptions = (enumber) => ({
  queryKey: ['employeeInfo', enumber],
  queryFn: () => getEmployeeInfo(enumber),
});

export {
  documentExecutorListQueryOptions,
  documentFileListQueryOptions,
  documentReceiverListQueryOptions,
  documentReferenceDocsQueryOptions,
  employeeInfoQueryOptions,
  temporaryDocDetailQueryOptions,
  useDeleteTemporaryDocMutation,
  useGetTemporaryDocListQuery,
};
